//asian-paints-frontend/src/pages/project/ProjectList.js
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import {
  CheckCircle2, X, Trash2, AlertTriangle, Archive,
  RefreshCw, Plus, FolderKanban, Loader2, Inbox,
  PlayCircle, Square, Clock, FileText, Search,
  Users, Phone, Mail, UserCheck, Briefcase,
} from "lucide-react";

// ─── AP DESIGN TOKENS ────────────────────────────────────────────────────────
const T = {
  violet:      "#5C2D91",
  violetMid:   "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red:         "#C8102E",
  redLight:    "#FCEAED",
  orange:      "#E8640A",
  orangeLight: "#FEF0E6",
  gold:        "#C87800",
  goldLight:   "#FEF5DC",
  green:       "#1A7A42",
  greenLight:  "#E2F5EC",
  bg:          "#F4F0FA",
  surface:     "#FFFFFF",
  surfaceAlt:  "#FAF8FD",
  dark:        "#1A0A2E",
  muted:       "#6B5A8A",
  faint:       "#C4B8DC",
  border:      "rgba(92,45,145,0.1)",
  borderSolid: "#E8E0F5",
};

const statusStyle = (status) => {
  if (!status) return { bg: T.violetLight, color: T.violet, dot: T.violet };
  const s = status.toLowerCase();
  if (s === "completed")   return { bg: T.greenLight,  color: T.green,  dot: T.green  };
  if (s === "in progress") return { bg: T.orangeLight, color: T.orange, dot: T.orange };
  if (s === "not started") return { bg: T.violetLight, color: T.violet, dot: T.violet };
  if (s === "on hold")     return { bg: T.goldLight,   color: T.gold,   dot: T.gold   };
  if (s === "delayed")     return { bg: T.redLight,    color: T.red,    dot: T.red    };
  if (s === "active")      return { bg: T.greenLight,  color: T.green,  dot: T.green  };
  if (s === "pending")     return { bg: T.goldLight,   color: T.gold,   dot: T.gold   };
  if (s === "deactive" || s === "archived") return { bg: T.violetLight, color: T.muted, dot: T.faint };
  if (s === "rejected")    return { bg: T.redLight,    color: T.red,    dot: T.red    };
  return { bg: T.violetLight, color: T.violet, dot: T.violet };
};

const isArchiveStatus = (status = "") => {
  const s = String(status).toLowerCase().trim();
  return s === "deactive" || s === "archived" || s === "completed";
};

const fmtDate = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};



// ─── Avatar initials ──────────────────────────────────────────────────────────
const Avatar = ({ name }) => {
  const initials = (name || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  const colors = ["#5C2D91","#C8102E","#E8640A","#1A7A42","#C87800","#7B3FB5"];
  const color  = colors[(name || "?").charCodeAt(0) % colors.length];
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 10, flexShrink: 0,
      background: `linear-gradient(135deg, ${color}CC, ${color})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 11, fontWeight: 700, color: "#fff",
      boxShadow: `0 2px 6px ${color}40`,
    }}>{initials}</div>
  );
};

// ─── Action button ────────────────────────────────────────────────────────────
const ActionBtn = ({ title, onClick, color, bg, border, children, disabled = false }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: disabled ? T.surfaceAlt : hov ? color : bg,
        color:      disabled ? T.faint      : hov ? "#fff" : color,
        border:     `1px solid ${disabled ? T.borderSolid : hov ? color : (border || color + "60")}`,
        padding: "7px 16px", borderRadius: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, flexShrink: 0, transition: "all 0.15s",
        opacity: disabled ? 0.5 : 1,
        boxShadow: hov && !disabled ? `0 4px 14px ${color}40` : "none",
        whiteSpace: "nowrap",
      }}
    >{children}</button>
  );
};

// ─── Modal component ──────────────────────────────────────────────────────────
function Modal({ modal, onClose }) {
  if (!modal) return null;
  const isConfirm = modal.type === "confirm";
  const isError   = modal.type === "error";
  const iconMap   = { success: <CheckCircle2 size={24} />, error: <X size={24} />, confirm: <Trash2 size={24} />, validation: <AlertTriangle size={24} />, archive: <Archive size={24} /> };
  const colorMap  = {
    success:    { accent: T.green,  light: T.greenLight,  border: "#A7F3D0" },
    error:      { accent: T.red,    light: T.redLight,    border: "#FECDD3" },
    confirm:    { accent: T.red,    light: T.redLight,    border: "#FECDD3" },
    archive:    { accent: T.muted,  light: T.violetLight, border: T.borderSolid },
    validation: { accent: T.orange, light: T.orangeLight, border: "#FED7AA" },
  };
  const c = colorMap[modal.type] || colorMap.success;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(26,10,46,0.45)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "plFadeIn 0.18s ease",
    }}>
      <style>{`
        @keyframes plFadeIn { from { opacity:0; transform:scale(0.94) } to { opacity:1; transform:scale(1) } }
      `}</style>
      <div style={{
        background: T.surface, borderRadius: 20, width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(92,45,145,0.22), 0 4px 16px rgba(0,0,0,0.10)",
        border: `1px solid ${T.borderSolid}`, overflow: "hidden",
      }}>
        {/* Accent top bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${c.accent})` }} />
        <div style={{ padding: "28px 28px 24px" }}>
          {/* Icon + title */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: c.light, border: `1.5px solid ${c.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>{iconMap[modal.type]}</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: T.dark, lineHeight: 1.25 }}>{modal.title}</div>
              {modal.subtitle && (
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{modal.subtitle}</div>
              )}
            </div>
          </div>
          {/* Message */}
          <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.65, margin: "0 0 24px", fontWeight: 400 }}>
            {modal.message}
          </p>
          {/* Buttons */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            {(isConfirm || modal.type === "archive") && (
              <button onClick={onClose} style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 600,
                background: T.surfaceAlt, color: T.muted,
                border: `1.5px solid ${T.borderSolid}`, cursor: "pointer",
               fontFamily: "Montserrat, sans-serif",
              }}>Cancel</button>
            )}
            <button
              onClick={() => { if (modal.onConfirm) modal.onConfirm(); onClose(); }}
              style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 700,
                background: (isConfirm || isError)
                  ? `linear-gradient(135deg,${T.red},#E02040)`
                  : modal.type === "archive"
                  ? `linear-gradient(135deg,${T.muted},${T.violet})`
                  : `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none", cursor: "pointer",
                fontFamily: "Montserrat, sans-serif",
                boxShadow: `0 4px 14px ${(isConfirm || isError) ? T.red : T.violet}40`,
              }}
            >
              {isConfirm ? "Delete" : modal.type === "archive" ? "Archive" : "OK"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Stakeholder Modal Component ─────────────────────────────────────────────
function StakeholderModal({ data, onClose }) {
  if (!data) return null;

  const clientName = data.client_name || data.client_full_name || "";
  const clientEmail = data.client_email || "";
  const clientPhone = data.client_phone || "";
  const clientDesignation = data.client_designation || "";
  const projectTitle = data.title || data.project || "—";
  const projectUid = data.project_uid || `PRJ${String(data.id).padStart(3, "0")}`;

  const hasClient = Boolean(clientName || clientEmail || clientPhone);
  const stakeholders = data.projectStakeholders || [];
  const loading = Boolean(data.loadingStakeholders);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(26,10,46,0.48)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        animation: "plFadeIn 0.18s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.surface,
          borderRadius: 20,
          width: "100%",
          maxWidth: 540,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(92,45,145,0.22), 0 4px 20px rgba(0,0,0,0.12)",
          border: `1px solid ${T.borderSolid}`,
          overflow: "hidden",
        }}
      >
        {/* Accent top gradient bar */}
        <div style={{ height: 4, background: "linear-gradient(90deg, #5C2D91, #0284C7, #0D9488)", flexShrink: 0 }} />

        {/* Modal Header */}
        <div style={{ padding: "22px 26px 16px", borderBottom: `1px solid ${T.borderSolid}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: "#E0F2FE",
                border: "1.5px solid #BAE6FD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0284C7",
                flexShrink: 0,
              }}
            >
              <Users size={20} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16.5, color: T.dark, letterSpacing: "-0.2px" }}>
                Stakeholder Information
              </div>
              <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 600, color: T.violet }}>{projectTitle}</span>
                <span style={{ color: T.faint }}>•</span>
                <span style={{ fontWeight: 700, color: T.muted }}>{projectUid}</span>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: T.surfaceAlt,
              border: `1px solid ${T.borderSolid}`,
              color: T.muted,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              transition: "all 0.15s",
            }}
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div
          style={{
            padding: "20px 26px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* 1. Primary Client / Stakeholder Card */}
          {hasClient ? (
            <div
              style={{
                background: "linear-gradient(135deg, #F8FAFC, #F1F5F9)",
                border: `1.5px solid ${T.borderSolid}`,
                borderRadius: 16,
                padding: "16px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {/* Top: Avatar + Name + Designation */}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 12,
                    background: "linear-gradient(135deg, #0284C7, #0369A1)",
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 17,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 14px rgba(2,132,199,0.30)",
                    flexShrink: 0,
                    textTransform: "uppercase",
                  }}
                >
                  {(clientName || "?").slice(0, 2)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "#0284C7", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Primary Stakeholder / Client
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 16.5, fontWeight: 800, color: T.dark, textTransform: "capitalize" }}>
                      {clientName || "—"}
                    </span>
                    {clientDesignation && (
                      <span
                        style={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          background: T.violetLight,
                          color: T.violet,
                          padding: "2px 9px",
                          borderRadius: 20,
                          textTransform: "capitalize",
                        }}
                      >
                        {clientDesignation}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom row inside same div: Phone & Email Address */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                  paddingTop: 10,
                  borderTop: `1px solid ${T.borderSolid}`,
                }}
              >
                {/* Phone Number */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "#ECFDF5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Phone size={13} color="#16A34A" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Phone Number
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {clientPhone ? (
                        <a href={`tel:${clientPhone}`} style={{ color: T.dark, textDecoration: "none" }}>
                          {clientPhone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>

                {/* Email Address */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "#FEF0E6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Mail size={13} color="#E8640A" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Email Address
                    </div>
                    <div
                      style={{ fontSize: 12, fontWeight: 600, color: T.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={clientEmail}
                    >
                      {clientEmail ? (
                        <a href={`mailto:${clientEmail}`} style={{ color: T.dark, textDecoration: "none" }}>
                          {clientEmail}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* 2. Project Contacts Section (Stakeholders added from Engineer portal / Project assignments) */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: T.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>Project Contacts</span>
                <span
                  style={{
                    background: T.violetLight,
                    color: T.violet,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "1px 7px",
                    borderRadius: 10,
                  }}
                >
                  {stakeholders.length}
                </span>
              </div>
              {loading && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.muted }}>
                  <Loader2 size={12} className="animate-spin" />
                  <span>Loading contacts...</span>
                </div>
              )}
            </div>

            {loading && stakeholders.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "24px 16px",
                  background: T.surfaceAlt,
                  borderRadius: 12,
                  border: `1px dashed ${T.borderSolid}`,
                  color: T.muted,
                  fontSize: 12.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <Loader2 size={15} className="animate-spin" />
                Loading project stakeholders...
              </div>
            ) : stakeholders.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {stakeholders.map((s, idx) => {
                  const initials = (s.name || "?")
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2);
                  const isApplicator = s.is_applicator || (s.designation || "").toLowerCase().includes("applicator");
                  const isPrimary = (clientEmail && s.email && clientEmail.toLowerCase() === s.email.toLowerCase()) ||
                                    (data.client_id && String(s.id) === String(data.client_id));
                  const roleBadge = s.designation || s.client_designation || (isApplicator ? "Applicator" : s.role === "admin" ? "Admin" : "Stakeholder");

                  return (
                    <div
                      key={s.id || idx}
                      style={{
                        background: isApplicator ? "#FFFDF5" : isPrimary ? "#F8FAFC" : T.surfaceAlt,
                        border: `1px solid ${isApplicator ? "#FEF3C7" : isPrimary ? "#BAE6FD" : T.borderSolid}`,
                        borderRadius: 12,
                        padding: "10px 14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: isApplicator ? "#FEF3C7" : isPrimary ? "#E0F2FE" : "#EDE6F7",
                            color: isApplicator ? "#B45309" : isPrimary ? "#0284C7" : "#5C2D91",
                            fontWeight: 800,
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {initials}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: T.dark, textTransform: "capitalize" }}>
                              {s.name}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                padding: "2px 8px",
                                borderRadius: 12,
                                background: isApplicator ? "#FEF3C7" : isPrimary ? "#E0F2FE" : T.violetLight,
                                color: isApplicator ? "#B45309" : isPrimary ? "#0284C7" : T.violet,
                                textTransform: "capitalize",
                              }}
                            >
                              {roleBadge}
                            </span>
                            {isPrimary && (
                              <span
                                style={{
                                  fontSize: 9.5,
                                  fontWeight: 700,
                                  padding: "2px 7px",
                                  borderRadius: 12,
                                  background: "#ECFDF5",
                                  color: "#16A34A",
                                  border: "1px solid #BBF7D0",
                                }}
                              >
                                Primary
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 4, flexWrap: "wrap" }}>
                            {s.phone && (
                              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.muted }}>
                                <Phone size={11} color="#16A34A" />
                                <a href={`tel:${s.phone}`} style={{ color: T.muted, textDecoration: "none" }}>
                                  {s.phone}
                                </a>
                              </div>
                            )}
                            {s.email && (
                              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.muted, minWidth: 0 }}>
                                <Mail size={11} color="#E8640A" />
                                <a
                                  href={`mailto:${s.email}`}
                                  style={{ color: T.muted, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                  title={s.email}
                                >
                                  {s.email}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : !hasClient ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "32px 16px",
                  background: T.surfaceAlt,
                  border: `1.5px dashed ${T.borderSolid}`,
                  borderRadius: 16,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: T.violetLight,
                    color: T.muted,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 10px",
                  }}
                >
                  <Users size={20} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: T.dark, marginBottom: 3 }}>
                  No Stakeholder Details Found
                </div>
                <div style={{ fontSize: 12.5, color: T.muted, maxWidth: 300, margin: "0 auto" }}>
                  This project does not have client contact or stakeholder details linked yet.
                </div>
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "16px 12px",
                  background: T.surfaceAlt,
                  borderRadius: 12,
                  border: `1px dashed ${T.borderSolid}`,
                }}
              >
                <span style={{ fontSize: 12, color: T.muted }}>
                  No additional stakeholders registered for this project yet.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "14px 26px",
            borderTop: `1px solid ${T.borderSolid}`,
            background: T.surface,
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "9px 24px",
              borderRadius: 11,
              fontSize: 13,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${T.violet}, ${T.violetMid})`,
              color: "#fff",
              border: "none",
              cursor: "pointer",
              boxShadow: `0 4px 14px ${T.violet}35`,
              transition: "all 0.15s",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ProjectList() {
const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("All");
  const [hov,      setHov]      = useState(null);
  const [page,     setPage]     = useState(1);
  const [modal,    setModal]    = useState(null);
  const [stakeholderModal, setStakeholderModal] = useState(null);
  const [rawProjects, setRawProjects] = useState([]);
  const PER_PAGE = 10;
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const showModal = (type, title, message, onConfirm = null, subtitle = "") =>
    setModal({ type, title, message, onConfirm, subtitle });
  const closeModal = () => setModal(null);

  const handleOpenStakeholder = async (project) => {
    setStakeholderModal({
      ...project,
      loadingStakeholders: true,
      projectStakeholders: [],
    });

    try {
      // 1. Fetch extra client details if not present on the row
      const projPromise = (!project.client_phone && !project.client_email && !project.client_designation)
        ? API.get(`/projects/${project.id}`)
        : Promise.resolve(null);

      // 2. Fetch project stakeholders (from engineer/project contacts route)
      const stPromise = API.get(`/engineers/me/projects/${project.id}/stakeholders`);

      const [projRes, stRes] = await Promise.allSettled([projPromise, stPromise]);

      let updatedData = {};

      if (projRes.status === "fulfilled" && projRes.value?.data?.success && projRes.value.data.project) {
        const p = projRes.value.data.project;
        updatedData = {
          client_name: p.client_full_name || project.client_name,
          client_email: p.client_email || project.client_email,
          client_phone: p.client_phone || project.client_phone,
          client_designation: p.client_designation || project.client_designation,
        };
      }

      let fetchedStakeholders = [];
      if (stRes.status === "fulfilled" && stRes.value?.data?.success) {
        fetchedStakeholders = stRes.value.data.stakeholders || stRes.value.data.data || [];
      }

      setStakeholderModal(prev => prev && prev.id === project.id ? {
        ...prev,
        ...updatedData,
        projectStakeholders: fetchedStakeholders,
        loadingStakeholders: false,
      } : prev);
    } catch (err) {
      console.error("Failed to load extra stakeholder details:", err);
      setStakeholderModal(prev => prev && prev.id === project.id ? {
        ...prev,
        loadingStakeholders: false,
      } : prev);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  // NEW: read ?status= from URL and pre-select the matching filter pill
  useEffect(() => {
    const statusParam = (searchParams.get("status") || "").toLowerCase();
    if (statusParam === "active")   setFilter("Active");
    else if (statusParam === "archived") setFilter("Archived");
    else if (statusParam === "rejected") setFilter("Rejected");
    // if no param or unrecognized value, leave filter as-is (defaults to "All")
  }, [searchParams]);

  // ── ALL API LOGIC UNCHANGED ───────────────────────────────────────────────
  const fetchProjects = async () => {
  try {
    setLoading(true);

    // ✅ Checks both is_admin === 1 AND role === 'admin' / 'super_admin' (matches dashboardController & ProjectReports)
    const userRole = (localStorage.getItem("role") || "").toLowerCase();
    const isAdmin = Number(localStorage.getItem("is_admin")) === 1 || userRole === "admin" || userRole === "super_admin";
    const managerId = localStorage.getItem("id");
    console.log("isAdmin:", isAdmin, "userRole:", userRole);
    let res;
    if (isAdmin) {
      res = await API.get("/projects");
    } else {
      res = await API.get("/projects/manager/" + managerId);
    }

   // ✅ handles both response shapes: { projects: [] } and { data: [] }
    const all = res.data.projects || res.data.data || [];
    setRawProjects(all);

    const approved = all.filter((p) => {
      const s = (p.status || "").toLowerCase();
      return s !== "pending";
    });

    setProjects(approved);
  } catch (err) {
    console.error("PROJECT FETCH ERROR:", err);
  } finally {
    setLoading(false);
  }
};

  // ── DELETE — window.confirm + alert replaced with modal ──────────────────
  const handleDelete = async (id) => {
    showModal(
      "confirm",
      "Delete Project",
      "Are you sure you want to delete this project? This action cannot be undone.",
      async () => {
        try {
          await API.delete(`/projects/${id}`);
          setProjects((p) => p.filter((pr) => pr.id !== id));
          showModal("success", "Project Deleted", "Project deleted successfully.");
        } catch (err) {
          showModal(
            "error",
            "Delete Failed",
            err.response?.data?.message || "Unable to delete the project. Please try again later."
          );
        }
      }
    );
  };

  // ── ARCHIVE — window.confirm + alert replaced with modal ─────────────────
  // Uses existing PUT /projects/:id endpoint — no API change
  const handleArchive = async (project) => {
    showModal(
      "archive",
      "Archive Project",
      `"${project.title || project.project_uid}" will be hidden from the default list and accessible only via the Archived filter.`,
      async () => {
        try {
          await API.put(`/projects/${project.id}`, { status: "Deactive" });
setProjects((prev) =>
  prev.map((p) => p.id === project.id ? { ...p, status: "Deactive" } : p)
);
        } catch (err) {
          showModal(
            "error",
            "Archive Failed",
            err.response?.data?.message || "Failed to archive project. Please try again."
          );
        }
      }
    );
  };

  // ── FILTER PILLS — unchanged ──────────────────────────────────────────────
  const filterPills = ["All", "Active", "Rejected", "Archived"];

  // ── FILTER LOGIC — unchanged ──────────────────────────────────────────────
const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (p.project_uid  || "").toLowerCase().includes(q) ||
      (p.title        || "").toLowerCase().includes(q) ||
      (p.service_type || "").toLowerCase().includes(q) ||
      (p.client_name  || "").toLowerCase().includes(q);

   const statusLower = (p.status || "").toLowerCase();
   let matchFilter;
   if (filter === "All") {
     matchFilter = !isArchiveStatus(p.status);
   } else if (filter === "Archived") {
     matchFilter = isArchiveStatus(p.status);
   } else {
     matchFilter = (p.status || "") === filter;
   }

    const projStart = p.start_date ? new Date(p.start_date) : null;
    const matchFrom = !dateFrom || (projStart && projStart >= new Date(dateFrom));
    const matchTo   = !dateTo   || (projStart && projStart <= new Date(dateTo));

    return matchSearch && matchFilter && matchFrom && matchTo;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged      = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  

// ── EXPORT — builds CSV from currently filtered rows, no backend call ─────
  const handleExport = () => {
    const headers = ["Project ID", "Title", "Segment", "Client", "Status", "Start Date", "End Date","TID","Engineer Name","Applicator Name","Region","Unit","Total Area"];
    const rows = filtered.map((p) => [
  p.project_uid || `PRJ${String(p.id).padStart(3, "0")}`,
  p.title || p.project || "",
  p.service_type || p.product_category || "",
  p.client_name || "",
      p.status || "",
      fmtDate(p.start_date),
      fmtDate(p.end_date),
      p.t_id || "",
      p.engineer_name || "",
      p.applicator_name || "",
      p.region || "",
      p.unit || "",
      p.total_area_sqmtr || "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `projects_export_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── KPI COUNTS — unchanged ────────────────────────────────────────────────
const stats = {
    total:    projects.length,
    active:   projects.filter((p) => (p.status || "").toLowerCase() === "active").length,
    archived: projects.filter((p) => isArchiveStatus(p.status)).length,
    pending:  rawProjects.filter((p) => (p.status || "").toLowerCase() === "pending").length,
  };

  return (
    <AdminLayout>
      {/* Modal — replaces all alert/confirm */}
      <Modal modal={modal} onClose={closeModal} />
      <StakeholderModal data={stakeholderModal} onClose={() => setStakeholderModal(null)} />

      <style>{`
       @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

        .pl-root * { font-family: 'Montserrat', sans-serif !important; }
        .pl-uid     { font-family: 'Montserrat', sans-serif !important; }

        .pl-row:hover { background: ${T.violetFaint} !important; }

        .pl-search:focus-within {
          border-color: ${T.violet} !important;
          box-shadow: 0 0 0 3px rgba(92,45,145,0.1) !important;
        }

        .pl-pill { transition: all 0.15s ease !important; }
        .pl-pill:hover { transform: translateY(-1px); }

        .pl-add-btn:hover {
          box-shadow: 0 6px 20px rgba(92,45,145,0.45) !important;
          transform: translateY(-1px);
        }
        .pl-add-btn { transition: all 0.18s ease !important; }

        .pl-refresh:hover { opacity: 0.85; }
        .pl-refresh { transition: opacity 0.15s; cursor: pointer; }
        @keyframes pl-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 1024px) {
          .pl-kpi-strip { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 768px) {
          .pl-header     { flex-direction: column !important; gap: 14px !important; }
          .pl-kpi-strip  { grid-template-columns: repeat(2,1fr) !important; }
          .pl-filter-row { flex-direction: column !important; align-items: flex-start !important; }
          .pl-table-wrap { font-size: 12px !important; }
          .pl-hide-md    { display: none !important; }
        }
        @media (max-width: 480px) {
          .pl-kpi-strip  { grid-template-columns: 1fr 1fr !important; }
          .pl-page-pad   { padding: 14px 12px !important; }
          .pl-hide-sm    { display: none !important; }
        }
      `}</style>

      <div className="pl-root pl-page-pad" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ── PAGE HEADER ── */}
        <div className="pl-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.violet},${T.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · Project Management
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
                Project List
              </h1>
              <span style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", fontSize: 9, fontWeight: 700,
                padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                textTransform: "uppercase", boxShadow: `0 2px 8px ${T.violet}40`,
              }}>ADMIN</span>
            </div>
            <p style={{ color: T.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              Approved projects — manage activities, reports and team assignments
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
<button
  className="pl-refresh"
  onClick={async () => {
    setRefreshing(true);
    const minSpin = new Promise(resolve => setTimeout(resolve, 700));
    await Promise.all([fetchProjects(), minSpin]);
    setRefreshing(false);
  }}
  disabled={refreshing}
  style={{
    width: 40, height: 40, borderRadius: 11,
    border: `1.5px solid ${T.borderSolid}`,
    background: T.surface, color: T.muted,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
    cursor: refreshing ? "not-allowed" : "pointer",
    opacity: refreshing ? 0.7 : 1,
  }}
  title="Refresh"
>
  <span style={{
    display: "inline-flex",
    animation: refreshing ? "pl-spin 0.7s linear infinite" : "none",
  }}>
    <RefreshCw size={16} />
  </span>
</button>

<button onClick={handleExport} style={{
            background: T.surface, color: T.violet,
            border: `1.5px solid ${T.violetMid}`,
            padding: "11px 20px", borderRadius: 12,
            fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 8,
            whiteSpace: "nowrap",
          }}>
            <FileText size={16} />
            Export 
          </button>

          <a href="/project-add" className="pl-add-btn" style={{
  background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
  color: "#fff", textDecoration: "none",
  padding: "11px 22px", borderRadius: 12,
  fontSize: 13, fontWeight: 700,
  display: "flex", alignItems: "center", gap: 8,
  boxShadow: `0 4px 14px ${T.violet}40`,
  whiteSpace: "nowrap",
}}>
  <Plus size={16} />
  Add Project
</a>
          </div>
        </div>

        {/* ── KPI STRIP — unchanged ── */}
        <div className="pl-kpi-strip" style={{
          display: "grid", gridTemplateColumns: "repeat(4,1fr)",
          gap: 16, marginBottom: 24,
        }}>
        {[
{ label: "Total Projects", value: stats.total,    icon: <FolderKanban size={22} />, color: T.violet, bg: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: "#D8C8F5" },
{ label: "Active",         value: stats.active,   icon: <RefreshCw size={22} />, color: T.green,  bg: `linear-gradient(135deg,${T.greenLight},#EAFAF0)`,  border: "#B0E8C8" },
{ label: "Pending",        value: stats.pending, icon: <Clock size={22} />, color: T.gold,    bg: `linear-gradient(135deg,${T.goldLight},#FFF8E8)`,    border: "#F0D8A0" },
{ label: "Archived",       value: stats.archived, icon: (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/>
    <rect x="1" y="3" width="22" height="5" rx="1"/>
    <line x1="12" y1="11" x2="12" y2="17"/>
    <polyline points="9 14 12 17 15 14"/>
  </svg>
), color: T.muted,  bg: `linear-gradient(135deg,${T.violetLight},#EEE8F8)`, border: "#C8B8E8" },
          ].map((s) => (
            <div key={s.label} style={{
              background: T.surface,
              borderRadius: 16,
              border: `1px solid ${T.borderSolid}`,
              padding: "20px 22px",
              display: "flex", alignItems: "center", gap: 14,
              boxShadow: "0 2px 12px rgba(92,45,145,0.06)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{
                position: "absolute", right: -10, top: -10,
                width: 70, height: 70, borderRadius: "50%",
                background: s.bg, opacity: 0.6,
              }} />
              <div style={{
                width: 46, height: 46, borderRadius: 13,
                background: s.bg,
                border: `1.5px solid ${s.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0, position: "relative",
              }}>{s.icon}</div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {s.label}
                </div>
                <div style={{ fontWeight: 800, fontSize: 28, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── MAIN TABLE CARD ── */}
        <div style={{
          background: T.surface,
          borderRadius: 20,
          border: `1px solid ${T.borderSolid}`,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(92,45,145,0.08)",
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />

          <div style={{ padding: "20px 24px" }}>

            {/* ── TOOLBAR ── */}
            <div className="pl-filter-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>
                  {filter === "Archived" ? "Archived Projects" : "Approved Projects"}
                </div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 400 }}>
                  {filtered.length} of {filter === "All" ? projects.filter(p => !isArchiveStatus(p.status)).length : filter === "Archived" ? stats.archived : projects.length} records
                  {search && <span style={{ color: T.violet, fontWeight: 600 }}> · filtered</span>}
                  {filter === "Archived" && <span style={{ color: T.muted, fontWeight: 600 }}> · archived only</span>}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {filterPills.map((f) => (
                    <button key={f} className="pl-pill" onClick={() => { setFilter(f); setPage(1); }} style={{
                      fontSize: 11, padding: "6px 14px", borderRadius: 20, fontWeight: 600,
                      border: `1.5px solid ${filter === f ? T.violet : T.borderSolid}`,
                      background: filter === f ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface,
                      color: filter === f ? "#fff" : T.muted,
                      cursor: "pointer",
                      boxShadow: filter === f ? `0 2px 8px ${T.violet}30` : "none",
                    }}>{f}</button>
                  ))}
                </div>

<input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  title="From date"
                  style={{
                    border: `1.5px solid ${T.borderSolid}`, borderRadius: 10,
                    padding: "8px 10px", fontSize: 12, color: T.dark,
                    background: T.surfaceAlt, outline: "none", cursor: "pointer",
                  }}
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  title="To date"
                  style={{
                    border: `1.5px solid ${T.borderSolid}`, borderRadius: 10,
                    padding: "8px 10px", fontSize: 12, color: T.dark,
                    background: T.surfaceAlt, outline: "none", cursor: "pointer",
                  }}
                />

                <div className="pl-search" style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: T.surfaceAlt,
                  border: `1.5px solid ${T.borderSolid}`,
                  borderRadius: 12, padding: "9px 14px",
                  minWidth: 240, transition: "all 0.2s",
                }}>
                 <Search size={15} color={T.faint} />
                  <input
                    type="text"
                    placeholder="Search title, client, UID..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    style={{
                      border: "none", background: "transparent",
                      fontSize: 13, color: T.dark, outline: "none",
                      width: "100%", fontWeight: 400,
                    }}
                  />
                  {search && (
                    <button onClick={() => setSearch("")} style={{
                      background: T.violetLight, border: "none", color: T.violet,
                      cursor: "pointer", fontSize: 11, width: 18, height: 18,
                      borderRadius: "50%", display: "flex", alignItems: "center",
                      justifyContent: "center", fontWeight: 700, flexShrink: 0,
                    }}>✕</button>
                  )}
                </div>
              </div>
            </div>

            {/* ── TABLE ── */}
            {loading ? (
              <div style={{
                textAlign: "center", padding: "64px 0",
                display: "flex", flexDirection: "column",
                alignItems: "center", gap: 16,
              }}>
                <div style={{
  width: 56, height: 56, borderRadius: 16,
  background: T.violetLight,
  display: "flex", alignItems: "center", justifyContent: "center",
}}>
  <Loader2 size={24} color={T.violet} />
</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: T.dark, marginBottom: 4 }}>Loading projects</div>
                  <div style={{ fontSize: 13, color: T.muted }}>Fetching your project data...</div>
                </div>
              </div>
            ) : paged.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "64px 0",
                display: "flex", flexDirection: "column",
                alignItems: "center", gap: 16,
              }}>
                <div style={{
  width: 64, height: 64, borderRadius: 18,
  background: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`,
  border: `2px dashed ${T.faint}`,
  display: "flex", alignItems: "center", justifyContent: "center",
}}>
  <Inbox size={28} color={T.violet} />
</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark, marginBottom: 6 }}>
                    {search
                      ? "No matching projects"
                      : filter === "Archived"
                      ? "No archived projects"
                      : "No approved projects yet"}
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, maxWidth: 320, lineHeight: 1.6 }}>
                    {search
                      ? `No results for "${search}". Try a different search term or clear the filter.`
                      : filter === "Archived"
                      ? "Projects you archive will appear here. Use the Archive action on any project row."
                      : "Projects will appear here once they're approved from the Project Approvals queue."}
                  </div>
                  {search && (
                    <button onClick={() => setSearch("")} style={{
                      marginTop: 14, background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                      color: "#fff", border: "none", padding: "9px 20px",
                      borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer",
                    }}>Clear Search</button>
                  )}
                </div>
              </div>
            ) : (
              <div className="pl-table-wrap" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 800 }}>
                  <thead>
                    <tr style={{ background: T.violetFaint }}>
                      {[
                        { label: "#",               center: true,  cls: "" },
                        { label: "Project ID",      center: false, cls: "" },
                        { label: "Title & Service", center: false, cls: "" },
                        { label: "Client",          center: false, cls: "pl-hide-sm" },
                        { label: "Status",          center: false, cls: "" },
                        { label: "Duration",        center: false, cls: "pl-hide-md" },
                        { label: "Actions",         center: false, cls: "" },
                      ].map((h) => (
                        <th key={h.label} className={h.cls} style={{
                          fontSize: 10, fontWeight: 700, color: T.muted,
                          textTransform: "uppercase", letterSpacing: "0.09em",
                          padding: "12px 16px",
                          textAlign: h.center ? "center" : "left",
                          borderBottom: `2px solid ${T.borderSolid}`,
                          borderTop: `1px solid ${T.borderSolid}`,
                          whiteSpace: "nowrap",
                          background: T.violetFaint,
                        }}>{h.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((project, i) => {
                      const sc     = statusStyle(project.status);
                      const rowNum = (page - 1) * PER_PAGE + i + 1;
                      const isLast = i === paged.length - 1;
                      const isArchived = isArchiveStatus(project.status);
                      return (
                        <tr
                          key={project.id}
                          className="pl-row"
                          onMouseEnter={() => setHov(project.id)}
                          onMouseLeave={() => setHov(null)}
                          style={{ background: T.surface, transition: "background 0.12s", cursor: "default" }}
                        >
                          {/* # */}
                          <td style={{ padding: "15px 16px", textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 26, height: 26, borderRadius: 8,
                              background: T.violetFaint,
                              fontWeight: 700, fontSize: 11, color: T.muted,
                            }}>{rowNum}</span>
                          </td>

                          {/* Project UID */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span className="pl-uid" style={{
                              display: "inline-block",
                              background: T.violetFaint,
                              border: `1px solid ${T.borderSolid}`,
                              color: T.violet, fontWeight: 600, fontSize: 12,
                              padding: "4px 10px", borderRadius: 8,
                              letterSpacing: "0.04em",
                            }}>
                              {project.project_uid || `PRJ${String(project.id).padStart(3, "0")}`}
                            </span>
                          </td>

                          {/* Title & Service */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: T.dark, marginBottom: 3 }}>
                              {project.title || "—"}
                            </div>
                            <div style={{
                              fontSize: 11, color: T.muted, fontWeight: 500,
                              display: "inline-flex", alignItems: "center", gap: 5,
                            }}>
                              <span style={{ width: 4, height: 4, borderRadius: "50%", background: T.faint, display: "inline-block" }} />
                              {project.service_type || "—"}
                            </div>
                          </td>

                          {/* Client */}
                          <td className="pl-hide-sm" style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{ fontSize: 13, color: T.dark, fontWeight: 500 }}>
                    {project.client_name || "—"}
                        </span>
                          </td>

                          {/* Status */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              background: sc.bg, color: sc.color,
                              fontSize: 11, fontWeight: 700,
                              padding: "5px 12px", borderRadius: 20,
                              whiteSpace: "nowrap",
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: sc.dot, flexShrink: 0 }} />
                              {project.status || "Unknown"}
                            </span>
                          </td>

                         {/* Duration */}
<td className="pl-hide-md" style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}`, whiteSpace: "nowrap" }}>
  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.dark, fontWeight: 500, whiteSpace: "nowrap" }}>
      <PlayCircle size={12} />
      {fmtDate(project.start_date)}
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.muted, whiteSpace: "nowrap" }}>
      <Square size={10} />
      {fmtDate(project.end_date)}
    </div>
  </div>
</td>

                         {/* Actions */}
<td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
<ActionBtn
  title="Edit Project"
  onClick={() => navigate(`/project-add?edit_id=${project.id}`)}
  color={T.violetMid} bg={T.violetLight} border={T.borderSolid}
>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
    </ActionBtn>
<ActionBtn
      title="Reports"
      onClick={() => navigate(`/project-reports?project_id=${project.id}`)}
      color={T.orange} bg={T.orangeLight} border="#FED7A0"
    >
      <FileText size={14} />
    </ActionBtn>
<ActionBtn
  title={isArchived ? "Already Archived" : "Archive Project"}
  onClick={() => !isArchived && handleArchive(project)}
  color={T.muted} bg="#F1F5F9" border="#CBD5E1"
  disabled={isArchived}
>
<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
  <polyline points="21 8 21 21 3 21 3 8"/>
  <rect x="1" y="3" width="22" height="5" rx="1"/>
  <line x1="12" y1="11" x2="12" y2="17"/>
  <polyline points="9 14 12 17 15 14"/>
</svg>
</ActionBtn>
    <ActionBtn
      title="Delete Project"
      onClick={() => handleDelete(project.id)}
      color={T.red} bg={T.redLight} border="#F8C8D0"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
    </ActionBtn>
    <ActionBtn
      title="Stakeholder Information"
      onClick={() => handleOpenStakeholder(project)}
      color="#0284C7" bg="#E0F2FE" border="#BAE6FD"
    >
      <Users size={14} />
    </ActionBtn>
  </div>
</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── PAGINATION — unchanged ── */}
            {!loading && filtered.length > 0 && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginTop: 20, paddingTop: 16,
                borderTop: `1px solid ${T.borderSolid}`,
                flexWrap: "wrap", gap: 12,
              }}>
                <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
                  Showing{" "}
                  <strong style={{ color: T.dark }}>
                    {Math.min((page - 1) * PER_PAGE + 1, filtered.length)}–{Math.min(page * PER_PAGE, filtered.length)}
                  </strong>
                  {" "}of{" "}
                  <strong style={{ color: T.dark }}>{filtered.length}</strong>
                  {" "}projects
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      height: 34, padding: "0 14px", borderRadius: 10,
                      border: `1.5px solid ${page === 1 ? T.borderSolid : T.violet}`,
                      background: page === 1 ? T.surface : T.violetLight,
                      color: page === 1 ? T.faint : T.violet,
                      cursor: page === 1 ? "not-allowed" : "pointer",
                      fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                    }}
                  >‹ Prev</button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                    .map((n) => (
                      <button key={n} onClick={() => setPage(n)} style={{
                        width: 34, height: 34, borderRadius: 10,
                        border: `1.5px solid ${n === page ? T.violet : T.borderSolid}`,
                        background: n === page ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface,
                        color: n === page ? "#fff" : T.muted,
                        fontSize: 12, fontWeight: 700, cursor: "pointer",
                        boxShadow: n === page ? `0 2px 8px ${T.violet}40` : "none",
                        transition: "all 0.15s",
                      }}>{n}</button>
                    ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      height: 34, padding: "0 14px", borderRadius: 10,
                      border: `1.5px solid ${page === totalPages ? T.borderSolid : T.violet}`,
                      background: page === totalPages ? T.surface : T.violetLight,
                      color: page === totalPages ? T.faint : T.violet,
                      cursor: page === totalPages ? "not-allowed" : "pointer",
                      fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                    }}
                  >Next ›</button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </AdminLayout>
  );
}